// src/components/MenuTree.jsx
import React, { useState, useMemo } from "react";
import Button1 from "@material-ui/core/Button";

// ─── Build tree structure ─────────────────────────────────────────────────────
function buildTree(menus) {
  const map  = {};
  menus.forEach(m => { map[m.MENU_ID] = { node: m, children: [] }; });
  const roots = [];
  menus.forEach(m => {
    if (m.MENU_PARENT_ID && map[m.MENU_PARENT_ID]) {
      map[m.MENU_PARENT_ID].children.push(map[m.MENU_ID]);
    } else {
      roots.push(map[m.MENU_ID]);
    }
  });
  const sort = nodes => {
    nodes.sort((a, b) => a.node.MENU_SEQ - b.node.MENU_SEQ);
    nodes.forEach(n => sort(n.children));
    return nodes;
  };
  return sort(roots);
}

// ─── Single tree row ──────────────────────────────────────────────────────────
function TreeNode({ nodeData, children, depth, selectedId, onSelect, openSet, onToggle }) {
  const { node } = nodeData;
  const hasChildren = children.length > 0;
  const isOpen      = openSet.has(node.MENU_ID);
  const isActive    = selectedId === node.MENU_ID;

  return (
    <div>
      {/* Row */}
      <div
        onClick={() => onSelect(node.MENU_ID)}
        style={{
          display:       "flex",
          alignItems:    "center",
          gap:           6,
          padding:       "6px 8px",
          paddingLeft:   depth * 16 + 8,
          cursor:        "pointer",
          borderRadius:  6,
          margin:        "1px 8px",
          background:    isActive ? "#eff6ff" : "transparent",
          color:         isActive ? "#1d4ed8" : "#1e293b",
          transition:    "background 0.1s",
          userSelect:    "none",
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
      >
        {/* Expand toggle */}
        <span
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node.MENU_ID); }}
          style={{
            width:      18,
            height:     18,
            display:    "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize:   12,
            color:      "#94a3b8",
            transform:  hasChildren && isOpen ? "rotate(90deg)" : "none",
            transition: "transform 0.15s",
            opacity:    hasChildren ? 1 : 0,
            flexShrink: 0,
          }}>›</span>

        {/* Icon */}
        <i
          className={`ti ${node.MENU_ICON || "ti-square"}`}
          style={{ fontSize: 15, color: isActive ? "#1d4ed8" : "#64748b", flexShrink: 0, width: 18, textAlign: "center" }}
        />

        {/* Label */}
        <span style={{
          flex: 1, fontSize: 13,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {node.MENU_TEXT}
        </span>

        {/* Active badge */}
        <span style={{
          fontSize:     10,
          padding:      "2px 6px",
          borderRadius: 10,
          fontWeight:   700,
          flexShrink:   0,
          background:   node.MENU_ACTIVE ? "#dcfce7" : "#f1f5f9",
          color:        node.MENU_ACTIVE ? "#16a34a" : "#94a3b8",
        }}>
          {node.MENU_ACTIVE ? "On" : "Off"}
        </span>
      </div>

      {/* Children */}
      {hasChildren && isOpen && (
        <div>
          {children.map(child => (
            <TreeNode
              key={child.node.MENU_ID}
              nodeData={child}
              children={child.children}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              openSet={openSet}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Sidebar ─────────────────────────────────────────────────────────────
export function MenuTree({ menus, selectedId, onSelect, onAdd }) {
  const [search,  setSearch]  = useState("");
  const [openSet, setOpenSet] = useState(new Set());

  const filtered = useMemo(() => {
    if (!search.trim()) return menus;
    const q = search.toLowerCase();
    const matched = new Set();
    const addAncestors = (id) => {
      const m = menus.find(x => x.MENU_ID === id);
      if (m) { matched.add(m.MENU_ID); if (m.MENU_PARENT_ID) addAncestors(m.MENU_PARENT_ID); }
    };
    menus.forEach(m => {
      if (
        m.MENU_TEXT.toLowerCase().includes(q) ||
        m.MENU_CODE.toLowerCase().includes(q)
      ) {
        matched.add(m.MENU_ID);
        if (m.MENU_PARENT_ID) addAncestors(m.MENU_PARENT_ID);
      }
    });
    return menus.filter(m => matched.has(m.MENU_ID));
  }, [menus, search]);

  const tree = useMemo(() => buildTree(filtered), [filtered]);

  const toggle = (id) => setOpenSet(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div style={{
      width:        300,
      flexShrink:   0,
      borderRight:  "1px solid #e2e8f0",
      display:      "flex",
      flexDirection: "column",
      background:   "#fff",
      height:       "100%",
    }}>
      {/* Header */}
      <div style={{
        padding:       "12px 14px",
        borderBottom:  "1px solid #e2e8f0",
        display:       "flex",
        alignItems:    "center",
        justifyContent: "space-between",
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>All Menus</span>
        <Button1
          variant="outlined"
          size="small"
          onClick={() => onAdd(null)}
          style={{ fontSize: 12, textTransform: "none", padding: "2px 10px" }}
        >
          + Add
        </Button1>
      </div>

      {/* Search */}
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>
        <input
          type="text"
          placeholder="Search menu..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width:        "100%",
            padding:      "6px 10px",
            fontSize:     13,
            border:       "1px solid #e2e8f0",
            borderRadius: 6,
            color:        "#1e293b",
            outline:      "none",
          }}
        />
      </div>

      {/* Tree */}
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
        {tree.length === 0
          ? <p style={{ padding: "16px", fontSize: 13, color: "#94a3b8" }}>No menus found.</p>
          : tree.map(n => (
            <TreeNode
              key={n.node.MENU_ID}
              nodeData={n}
              children={n.children}
              depth={0}
              selectedId={selectedId}
              onSelect={onSelect}
              openSet={openSet}
              onToggle={toggle}
            />
          ))
        }
      </div>
    </div>
  );
}
